<template>
  <v-card class="mb-4" :loading="loading" outlined>
    <v-toolbar color="titleBar" dark flat dense>
      <v-toolbar-title>{{ $t("cdrs") }}</v-toolbar-title>
      <v-spacer />

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <date-picker
            v-on="on"
            v-model="dates"
            :allowedDates="dateAllowed"
            icon
            range
            sort-range
            left
            bottom
            rounded
          />
        </template>
        <span>{{ $t("query period") }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="loading"
            :loading="loading"
            v-on="on"
            @click="
              getItems();
              getTotals();
            "
            icon
          >
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("reload") }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-card-text>
      <data-table-extended
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="itemsTotal"
        :loading="loadingItems"
        :disable-sort="loadingItems"
        :mobile-breakpoint="mbreakpoint"
        :hide-default-footer="itemsTotal <= 15"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [15, 50, 100, 1000],
        }"
      >
        <template v-slot:top="{ item }">
          <v-text-field
            v-model="search"
            append-outer-icon="mdi-magnify"
            clear-icon="mdi-close-circle"
            :disabled="loadingItems"
            :clearable="!isMobile"
            :label="$t('search')"
            single-line
            hide-details
            @click:append-outer="getItems"
            @click:clear="search = ''"
            @keyup.enter.native="getItems"
          />
        </template>
        <template v-slot:item.direction="{ item }">
          <v-icon v-if="item.direction == 'incoming'"
            >mdi-phone-incoming</v-icon
          >
          <v-icon v-else>mdi-phone-outgoing</v-icon>
        </template>
        <template v-slot:item.call_start_datetime="{ item }">
          <span class="monospace text-no-wrap">
            {{
              item.call_start_datetime
                ? formatDateTime(item.call_start_datetime)
                : "-"
            }}
          </span>
        </template>
        <template v-slot:item.call_end_datetime="{ item }">
          <span class="monospace text-no-wrap">
            {{
              item.call_end_datetime
                ? formatDateTime(item.call_end_datetime)
                : "-"
            }}
          </span>
        </template>
        <template v-slot:item.duration="{ item }">
          <span class="monospace text-no-wrap">
            {{
              item.call_start_datetime && item.call_end_datetime
                ? formatDurationMs(itemDuration(item), false, true)
                : "-"
            }}
          </span>
        </template>
        <template v-slot:item.destination_number="{ item }">
          <span class="monospace text-no-wrap">
            {{ item.destination_number ? item.destination_number : "-" }}
          </span>
        </template>
        <template v-slot:item.source_number="{ item }">
          <span class="monospace text-no-wrap">
            {{ item.source_number ? item.source_number : "-" }}
          </span>
        </template>
        <template v-slot:item.costs="{ item }">
          <span class="monospace text-no-wrap">
            {{ item.cost ? formatMoney(item.cost / 1_000_000, "CHF") : "-" }}
          </span>
        </template>
        <template v-slot:body.append>
          <tr class="text-subtitle-2">
            <td :colspan="headers.length" class="text-center">
              <v-progress-linear indeterminate v-if="loadingTotals" />
              <div v-else>
                <v-chip class="ma-2" outlined small v-if="dates.length > 1">
                  <v-icon left> mdi-calendar-range </v-icon>
                  {{ $t("period") }}:
                  {{ formatDate(dates[0]) + " - " + formatDate(dates[1]) }}
                </v-chip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-chip v-on="on" class="ma-2" outlined small>
                      <v-icon left> mdi-cash </v-icon>
                      {{ $t("costs") }}:
                      {{
                        totalCost != null
                          ? formatMoney(totalCost / 1_000_000, "CHF")
                          : "-"
                      }}
                    </v-chip>
                  </template>
                  <span>{{ $t("totalCostHint") }}</span>
                </v-tooltip>
                <v-chip class="ma-2" outlined small>
                  <v-icon left> mdi-timelapse </v-icon>
                  {{ $t("call duration") }}:
                  {{
                    totalDuration != null
                      ? formatDurationMs(totalDuration, false, true)
                      : "-"
                  }}
                </v-chip>
                <v-chip class="ma-2" outlined small>
                  <v-icon left> mdi-phone </v-icon>
                  {{ $t("total calls") }}:
                  {{ totalCalls != null ? totalCalls : "-" }}
                </v-chip>
                <v-chip class="ma-2" outlined small>
                  <v-icon left> mdi-phone-incoming </v-icon>
                  {{ $t("incomming calls") }}:
                  {{ totalCallsIncoming ? totalCallsIncoming : "-" }}
                </v-chip>
                <v-chip class="ma-2" outlined small>
                  <v-icon left> mdi-phone-outgoing </v-icon>
                  {{ $t("outgoing calls") }}:
                  {{ totalCallsOutgoing ? totalCallsOutgoing : "-" }}
                </v-chip>
              </div>
            </td>
          </tr>
        </template>
      </data-table-extended>
    </v-card-text>
  </v-card>
</template>

<script>
import formatDateTime from "@/utils/mixins/formatDateTime";
import formatDate from "@/utils/mixins/formatDate";
import formatMoney from "@/utils/mixins/formatMoney";
import formatNumber from "@/utils/mixins/formatNumber";
import formatDuration from "@/utils/mixins/formatDuration";
import formatDurationMs from "@/utils/mixins/formatDurationMs";
import formatPhoneNumber from "@/utils/mixins/formatPhoneNumber";
import showErrors from "@/utils/mixins/showErrors";
import isMobile from "@/utils/mixins/isMobile";
import DataTableExtended from "@/components/basics/DataTableExtended";
import BooleanValue from "@/components/basics/BooleanValue";
import DatePicker from "@/components/basics/DatePicker";

export default {
  name: "Cdrs",
  mixins: [
    formatDateTime,
    formatDate,
    formatMoney,
    formatNumber,
    formatDuration,
    formatDurationMs,
    formatPhoneNumber,
    showErrors,
    isMobile,
  ],
  components: {
    DataTableExtended,
    BooleanValue,
    DatePicker,
  },
  props: {
    productId: {
      validator: (prop) => typeof prop === "string" || typeof prop === "number",
    },
  },
  data() {
    return {
      items: [],
      loadingItems: false,
      dates: [
        this.$moment(new Date()).subtract(30, "days").format("YYYY-MM-DD"),
        this.$moment(new Date()).format("YYYY-MM-DD"),
      ],
      search: "",
      itemsPerPage: 50,
      itemsTotal: 0,
      options: { page: 1, itemsPerPage: 50 },
      totalDuration: null,
      totalCost: null,
      totalCalls: null,
      totalCallsIncoming: null,
      totalCallsOutgoing: null,
      loadingTotals: false,
    };
  },
  watch: {
    productId: {
      immediate: true,
      handler(value) {
        this.options.page = 1;
        this.getItems();
        this.getTotals();
      },
    },
    dates(value) {
      if (value && value.length == 2) {
        this.options.page = 1;
        this.getItems();
        this.getTotals();
      }
    },
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },
  computed: {
    loading() {
      return this.loadingItems || this.loadingTotals;
    },
    headers() {
      return [
        {
          text: this.$i18n.t("direction"),
          value: "direction",
          align: "left",
          sortable: false,
        },
        {
          text: this.$i18n.t("call start"),
          value: "call_start_datetime",
          align: "right",
          sortable: false,
        },
        {
          text: this.$i18n.t("call end"),
          value: "call_end_datetime",
          align: "right",
          sortable: false,
        },
        {
          text: this.$i18n.t("duration"),
          value: "duration",
          align: "left",
          sortable: false,
        },
        {
          text: this.$i18n.t("called number"),
          value: "destination_number",
          align: "left",
          sortable: false,
        },
        {
          text: this.$i18n.t("calling number"),
          value: "source_number",
          align: "left",
          sortable: false,
        },
        {
          text: this.$i18n.t("costs"),
          value: "costs",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    itemDuration(item) {
      return (
        Date.parse(item.call_end_datetime) -
        Date.parse(item.call_start_datetime)
      );
    },
    getDateParams() {
      return {
        call_date_after:
          this.dates.length > 0
            ? this.$moment(this.dates[0]).format("YYYY-MM-DDT00:00:00Z")
            : null,
        call_date_before:
          this.dates.length > 1
            ? this.$moment(this.dates[1]).format("YYYY-MM-DDT23:59:59Z")
            : null,
      };
    },
    getParams() {
      const { page, itemsPerPage } = this.options;
      return {
        ...this.getDateParams(),
        page_size: itemsPerPage || 15,
        page_number: (page || 1) - 1,
        q: this.search
          ? this.formatPhoneNumber(this.search, "CH", false, true)
          : null,
      };
    },
    dateAllowed(date) {
      const today = this.$moment(new Date()).format("YYYY-MM-DD");
      return (
        this.$moment(today).isSame(date, "day") ||
        this.$moment(date).isBefore(today)
      );
    },
    getItems() {
      var that = this;
      this.loadingItems = true;
      this.$http
        .get("services/voip/" + this.productId + "/cdrs", {
          params: this.getParams(),
        })
        .then((response) => {
          that.items = response.data.results.map(function (cdr) {
            return {
              ...cdr,
              product_id: that.productId,
            };
          });
          that.itemsTotal = response.data.count;
          that.totalCalls = response.data.count;
        })
        .catch((err) => {
          that.$store.commit("setSystemError", {
            msg: err.message,
            title: err.title,
          });
        })
        .finally(function () {
          that.loadingItems = false;
        });
    },
    getTotals() {
      var that = this;
      this.loadingTotals = true;
      this.$http
        .get("services/voip/" + this.productId + "/cdrs-summary", {
          params: this.getDateParams(),
          disableDefaultErrorHandler: true,
        })
        .then((response) => {
          that.totalCost = response.data.total_cost;
          that.totalDuration = response.data.total_duration_ms;
          that.totalCalls = response.data.total_calls;
          that.totalCallsIncoming = response.data.total_calls_incoming;
          that.totalCallsOutgoing = response.data.total_calls_outgoing;
        })
        .catch((err) => {
          if (err.status == 400) {
            // do nothing
          } else
            that.$store.commit("setSystemError", {
              msg: err.message,
              title: err.title,
            });
        })
        .finally(function () {
          that.loadingTotals = false;
        });
    },
  },
};
</script>

<i18n>
{
  "en": {
    "cdrs": "Connection details",
    "reload": "reload",
    "direction": "Direction",
    "call start": "Call start",
    "call end": "Call end",
    "duration": "Call duration",
    "called number": "Called number",
    "calling number": "Calling number",
    "costs": "Costs",
    "query period": "Query period",
    "search": "Search",
    "totals": "Totals",
    "outgoing calls": "Outgoing calls",
    "incomming calls": "Incomming calls",
    "total calls": "Total calls",
    "call duration": "Call duration",
    "period": "Period",
    "totalCostHint": "Please note that we bill call charges in micro-francs (1 CHF / 1,000,000). This may result in rounding discrepancies between the total call charges and the individual call charges displayed."
  },
  "de": {
    "cdrs": "Verbindungsnachweis",
    "reload": "aktualisieren",
    "direction": "Richtung",
    "call start": "Zeit Start",
    "call end": "Zeit Ende",
    "duration": "Gesprächsdauer",
    "called number": "Angerufende Nummer",
    "calling number": "Anrufende Nummer",
    "costs": "Kosten",
    "query period": "Abfragezeitraum",
    "search": "Suche",
    "totals": "Total",
    "outgoing calls": "Ausgehende Anrufe",
    "incomming calls": "Eingehende Anrufe",
    "total calls": "Anrufe insgesamt",
    "call duration": "Gesprächsdauer",
    "period": "Zeitraum",
    "totalCostHint": "Bitte beachten Sie, das wir Gesprächsgebühren in Micro-Franken (1 CHF / 1.000.000) abrechnen. Dadurch kommt es bei der Summe aller Gesprächsgebühren zu Rundungsabweichungen gegenüber den angezeigten  Einzelgesprächsgebühren."
  }
}
</i18n>
